"use client";

/**
 * <ChartChatSidebar> — AI 채팅 사이드바 (차트 분석 탭 우측)
 *
 * Phase 1: 단일 응답 · 메시지 히스토리 유지 · 전송 중 로딩
 * 이후 phase: streaming · @멘션 태그 · 우클릭으로 차트 요소 첨부
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Send, Loader2, Sparkles, MessageSquare, X } from "lucide-react";
import { getCsrfToken, CSRF_HEADER } from "@/lib/csrf";
import type { ChatMention } from "@/types/chart-lab";
import { MarkdownRenderer } from "@/components/report/markdown-renderer";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  ts: number;
}

// re-export (기존 import 체인 유지)
export type { ChatMention };

interface Props {
  symbol: string;
  /** 외부에서 멘션 주입 (우클릭 태깅용) */
  externalMentions?: ChatMention[];
  /** 입력창 아래 고정 footer slot — AI 차트 생성 · 이 기업 분석 버튼 등 */
  footer?: React.ReactNode;
}

// 사용자 결정 2026-05-09 — 차트 위주 → 기업 위주.
function buildWelcome(symbol: string): string {
  const t = (symbol || "이 종목").toUpperCase();
  return `${t}에 대해 뭐든지 물어보세요. 실적 / 재무 / 경쟁사 / 진입 타이밍 / 리스크 포인트 등.`;
}

export function ChartChatSidebar({ symbol, externalMentions, footer }: Props) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mentions, setMentions] = useState<ChatMention[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // 외부 멘션 동기화
  useEffect(() => {
    if (externalMentions && externalMentions.length > 0) {
      setMentions((prev) => {
        const existing = new Set(prev.map((m) => m.id));
        const added = externalMentions.filter((m) => !existing.has(m.id));
        if (added.length === 0) return prev;
        // 멘션 추가 시 input 포커스
        inputRef.current?.focus();
        return [...prev, ...added];
      });
    }
  }, [externalMentions]);

  // 스크롤 맨 아래로
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // 심볼 바뀌면 히스토리 초기화
  useEffect(() => {
    setMessages([]);
    setMentions([]);
    setError(null);
  }, [symbol]);

  const send = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;

    const userMsg: ChatMessage = { role: "user", content: trimmed, ts: Date.now() };
    const history = [...messages, userMsg];
    setMessages(history);
    setInput("");
    setError(null);
    setLoading(true);

    // Streaming — assistant 메시지를 빈 상태로 추가 후 delta 로 append
    let accumulated = "";
    setMessages((prev) => [...prev, { role: "assistant", content: "", ts: Date.now() }]);

    try {
      const token = await getCsrfToken();
      const res = await fetch(`/api/ticker/chart-chat-stream/${encodeURIComponent(symbol)}`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          ...(token ? { [CSRF_HEADER]: token } : {}),
        },
        body: JSON.stringify({
          messages: history.map((m) => ({ role: m.role, content: m.content })),
          mentions: mentions.map((m) => ({ type: m.type, label: m.label, data: m.data })),
        }),
      });
      if (!res.ok || !res.body) {
        const body = await res.text().catch(() => "");
        throw new Error(
          res.status === 401 ? "로그인이 필요합니다."
          : res.status === 402 ? "Premium 구독이 필요합니다."
          : body || `HTTP ${res.status}`
        );
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let streamEnded = false;
      while (!streamEnded) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // SSE 이벤트 파싱 — 이벤트는 \n\n 으로 구분
        const events = buffer.split(/\n\n/);
        buffer = events.pop() || "";
        for (const evt of events) {
          const lines = evt.split("\n");
          let eventType = "message";
          let dataStr = "";
          for (const line of lines) {
            if (line.startsWith("event:")) eventType = line.slice(6).trim();
            else if (line.startsWith("data:")) dataStr += line.slice(5).trim();
          }
          if (!dataStr) continue;
          if (eventType === "done") {
            streamEnded = true;
            break;
          }
          if (eventType === "error") {
            try {
              const parsed = JSON.parse(dataStr) as { message?: string };
              throw new Error(parsed.message || "스트림 에러");
            } catch (parseError) {
              if (parseError instanceof Error && parseError.message !== "스트림 에러") {
                throw parseError;
              }
              throw new Error("스트림 에러");
            }
          }
          try {
            const payload = JSON.parse(dataStr) as { delta?: string };
            if (payload.delta) {
              accumulated += payload.delta;
              // 마지막 메시지 (방금 추가한 assistant) 의 content 업데이트
              setMessages((prev) => {
                if (prev.length === 0) return prev;
                const last = prev[prev.length - 1];
                if (last.role !== "assistant") return prev;
                return [...prev.slice(0, -1), { ...last, content: accumulated }];
              });
            }
          } catch { /* delta 파싱 실패는 skip */ }
        }
      }

      // 응답이 비어있으면 placeholder
      if (!accumulated.trim()) {
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.role === "assistant" && !last.content) {
            return [...prev.slice(0, -1), { ...last, content: "응답이 비어 있습니다." }];
          }
          return prev;
        });
      }
      setMentions([]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "네트워크 오류";
      setError(msg);
      // 빈 assistant 메시지 제거 (rollback)
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === "assistant" && !last.content) return prev.slice(0, -1);
        return prev;
      });
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, mentions, symbol]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const welcome = buildWelcome(symbol);

  return (
    <div className="ccs-wrap">
      <div ref={scrollRef} className="ccs-scroll">
        {messages.length === 0 && (
          <div className="ccs-welcome ccs-welcome--centered">
            <MessageSquare className="w-4 h-4 text-[var(--bs-gray-400)]" />
            <p>{welcome}</p>
            <ul className="ccs-suggest">
              <li onClick={() => setInput("지금 진입하기에 좋은 자리인가? 기술적·펀더멘털 관점에서.")}>
                지금 진입하기 좋은 자리?
              </li>
              <li onClick={() => setInput("최근 분기 실적 어땠어? 컨센서스 대비 + 가이던스.")}>
                최근 실적 어땠어?
              </li>
              <li onClick={() => setInput("재무 건전성 평가해줘. 현금 / 부채 / 영업이익률 추세.")}>
                재무 건전성 평가
              </li>
              <li onClick={() => setInput("경쟁사 대비 어떤 위치야? 시장점유율 + 차별화 요인.")}>
                경쟁사 대비 위치
              </li>
            </ul>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`ccs-msg ccs-msg--${m.role}`}>
            {/* 사용자 결정 #10: assistant 메시지는 markdown 파싱 (** 강조 / 리스트 등). user 는 plain text. */}
            {m.role === "assistant" ? (
              <div className="ccs-msg-body ccs-msg-body--md">
                <MarkdownRenderer content={m.content || ""} />
              </div>
            ) : (
              <div className="ccs-msg-body">{m.content}</div>
            )}
          </div>
        ))}

        {loading && (
          <div className="ccs-msg ccs-msg--assistant ccs-msg--loading">
            <div className="ccs-msg-body">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>차트 보고 있어요…</span>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="ccs-error" role="alert">
          {error}
        </div>
      )}

      {mentions.length > 0 && (
        <div className="ccs-mentions">
          {mentions.map((m) => (
            <span key={m.id} className="ccs-chip" title={m.type}>
              @{m.label}
              <button
                type="button"
                className="ccs-chip-x"
                onClick={() => setMentions((prev) => prev.filter((x) => x.id !== m.id))}
                aria-label="멘션 제거"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="ccs-input-wrap">
        <textarea
          ref={inputRef}
          className="ccs-input"
          rows={2}
          placeholder={symbol ? `${symbol}에 대해 무엇이든 물어보세요` : "무엇이든 물어보세요"}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
          maxLength={2000}
        />
        <button
          type="button"
          className="ccs-send"
          onClick={send}
          disabled={loading || !input.trim()}
          aria-label="전송"
          title="Enter"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>

      {footer && <div className="ccs-footer">{footer}</div>}
    </div>
  );
}
